/**
 * Extract candidate's actual name from resume text
 * Looks for name patterns at the beginning of the resume
 */
export const extractCandidateName = (resumeText) => {
  if (!resumeText || resumeText.trim().length === 0) {
    return null;
  }

  // Get first few lines of resume (where name usually appears)
  const lines = resumeText.split('\n').filter(line => line.trim().length > 0);

  if (lines.length === 0) return null;

  // First line is usually the name
  const firstLine = lines[0].trim();

  // Clean up common resume artifacts
  const cleanedName = firstLine
    .replace(/[^\w\s.-]/g, '') // Remove special characters except spaces, dots, hyphens
    .replace(/\s+/g, ' ') // Normalize spaces
    .trim();

  // Validate it looks like a name (2-50 chars, contains letters)
  if (cleanedName.length >= 2 &&
    cleanedName.length <= 50 &&
    /[a-zA-Z]/.test(cleanedName) &&
    !cleanedName.toLowerCase().includes('resume') &&
    !cleanedName.toLowerCase().includes('curriculum')) {
    return cleanedName;
  }

  // Try second line if first didn't work
  if (lines.length > 1) {
    const secondLine = lines[1].trim()
      .replace(/[^\w\s.-]/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    if (secondLine.length >= 2 &&
      secondLine.length <= 50 &&
      /[a-zA-Z]/.test(secondLine) &&
      !secondLine.toLowerCase().includes('resume') &&
      !secondLine.toLowerCase().includes('curriculum')) {
      return secondLine;
    }
  }

  return null;
};

/**
 * Enrich candidate object with actual name from resume
 */
export const enrichCandidateWithName = async (candidate, resumeApiUrl, cachedResumes = null) => {
  try {
    let resumes = cachedResumes;

    if (!resumes) {
      // Get all loaded resumes if not provided
      const response = await fetch(`${resumeApiUrl}/load_resumes_to_model`);

      // Check if response is OK
      if (!response.ok) {
        console.error('Failed to fetch resumes:', response.status, response.statusText);
        return candidate;
      }

      // Check content type
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error('Response is not JSON:', contentType);
        const text = await response.text();
        console.error('Response text:', text.substring(0, 200));
        return candidate;
      }

      resumes = await response.json();
    }

    // Find matching resume with prioritized logic to avoid false positives (e.g. "Resume.pdf" matching "Tharun_Resume1.pdf")

    // 1. Exact match on file_name (Most reliable)
    let resume = resumes.find(r => r.file_name === candidate.name);

    // 2. Exact match on id
    if (!resume) {
      resume = resumes.find(r => r.id === candidate.name);
    }

    // 3. Case-insensitive match on file_name
    if (!resume && candidate.name) {
      resume = resumes.find(r => r.file_name && r.file_name.toLowerCase() === candidate.name.toLowerCase());
    }

    // 4. Case-insensitive match on id
    if (!resume && candidate.name) {
      resume = resumes.find(r => r.id && r.id.toLowerCase() === candidate.name.toLowerCase());
    }

    // 5. Fallback: Check if candidate.name looks like a filename but missing extension, or vice versa
    // useful if LLM outputs "MyResume" but file is "MyResume.pdf"
    if (!resume && candidate.name) {
      // 5. Fallback: Check if candidate.name looks like a filename but missing extension, or vice versa
      // useful if LLM outputs "MyResume" but file is "MyResume.pdf"
      if (!resume && candidate.name) {
        // Find potential match
        const potentialMatch = resumes.find(r =>
          (r.file_name && r.file_name.startsWith(candidate.name)) ||
          (r.id && candidate.name.startsWith(r.id))
        );

        if (potentialMatch) {
          // Verify it's not just a generic substring match which caused issues before
          const hasRiskyId = potentialMatch.id && potentialMatch.id.length < 5;

          // Only accept if ID is long enough OR if it's a very strong match
          if (!hasRiskyId) {
            resume = potentialMatch;
          }
        }
      }
    }

    if (resume) {
      const enriched = {
        ...candidate,
        // Store the actual S3 file name (with extension) to display in the UI
        fileName: resume.file_name || candidate.name,
        resumeFileName: resume.file_name || candidate.name
      };

      if (resume.resume_text) {
        const actualName = extractCandidateName(resume.resume_text);
        if (actualName) {
          enriched.actualName = actualName;
        }
      }
      return enriched;
    }
  } catch (error) {
    console.error('Error enriching candidate with name:', error);
  }

  return candidate;
};
